/**
 * SpeedDialFab
 *
 * A sprouting speed-dial floating action button for the Brand Portfolio page.
 * Four actions sprout vertically upward from the main FAB with staggered animation.
 *
 * Animation: CSS transitions via Tailwind classes - no external animation library.
 * Respects prefers-reduced-motion: disables translate + stagger, shows/hides instantly.
 *
 * Accessibility:
 * - Main button: role="button" aria-expanded aria-haspopup="menu"
 * - Action list: role="menu" aria-label
 * - Each action: role="menuitem" aria-label
 * - Focus trap: Tab cycles through actions + main button
 * - ESC closes
 */

import { useEffect, useRef, useCallback, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { usePublishFloatingHeight } from '@/hooks/usePublishFloatingHeight';
import { LockBadge } from '@/components/gating/LockBadge';

// ── Types ─────────────────────────────────────────────────────────────────────

export type SpeedDialActionId = 'compose' | 'create_brand' | 'plan_batch' | 'send_digest';

export interface SpeedDialAction {
  id: SpeedDialActionId;
  iconClass: string;
  labelKey: string;
  defaultLabel: string;
  accentBg: string;
  /** Icon colour matched to the bg shade (contrast token), never hardcoded white */
  accentText: string;
  accentRing: string;
  ariaLabel: string;
}

interface SpeedDialFabProps {
  isOpen: boolean;
  onToggle: () => void;
  onClose: () => void;
  onAction: (actionId: SpeedDialActionId) => void;
  /** Actions the viewer may not use (e.g. create brand for agency managers) */
  hiddenActions?: SpeedDialActionId[];
  /** Actions the agency package lacks: still shown, with a lock badge (value = portfolio feature key, used for the test id). The caller's onAction opens the upsell. */
  lockedActions?: Partial<Record<SpeedDialActionId, string>>;
  /** Mobile: sit in the empty centre slot of the two-item agency bottom nav. Desktop is unaffected. */
  centerOnMobile?: boolean;
}

// ── Action Definitions ────────────────────────────────────────────────────────

const ACTIONS: SpeedDialAction[] = [
  // Bottom-most (closest to FAB), appears last in DOM order for upward sprout
  {
    id: 'compose',
    iconClass: 'hm-pen-paper',
    labelKey: 'fab_compose_status',
    defaultLabel: 'Compose status',
    accentBg: 'bg-primary-500 hover:bg-primary-600',
    accentText: 'text-primary-contrast',
    accentRing: 'focus-visible:border-gray-900',
    ariaLabel: 'Compose status update',
  },
  {
    id: 'create_brand',
    iconClass: 'hm-plus-circle',
    labelKey: 'fab_create_brand_space',
    defaultLabel: 'Create brand',
    accentBg: 'bg-coral-500 hover:bg-coral-600',
    accentText: 'text-coral-contrast',
    accentRing: 'focus-visible:border-gray-900',
    ariaLabel: 'Create brand',
  },
  {
    id: 'plan_batch',
    iconClass: 'hm-calendar',
    labelKey: 'fab_plan_batch',
    defaultLabel: 'Plan batch',
    accentBg: 'bg-peach hover:bg-coral-300',
    accentText: 'text-gray-900',
    accentRing: 'focus-visible:border-gray-900',
    ariaLabel: 'Plan a content batch',
  },
  // Top-most (furthest from FAB), appears first in DOM order
  {
    id: 'send_digest',
    iconClass: 'hm-paper-plane',
    labelKey: 'fab_send_digest',
    defaultLabel: 'Send digest',
    accentBg: 'bg-secondary-600 hover:bg-secondary-700',
    accentText: 'text-secondary-600-contrast',
    accentRing: 'focus-visible:border-gray-900',
    ariaLabel: 'Send portfolio digest',
  },
];

// Sprout order (bottom to top): compose appears first (index 0), send_digest last
// We render in reverse visual order so the "lowest" action (compose) is closest to FAB
const SPROUT_ORDER: SpeedDialActionId[] = ['compose', 'create_brand', 'plan_batch', 'send_digest'];

// ── Component ─────────────────────────────────────────────────────────────────

export const SpeedDialFab = ({ isOpen, onToggle, onClose, onAction, hiddenActions, lockedActions, centerOnMobile = false }: SpeedDialFabProps) => {
  const { t } = useTranslation('agency');
  const containerRef = useRef<HTMLDivElement>(null);
  const mainBtnRef = useRef<HTMLButtonElement | null>(null);
  // Publish the toggle's real width (label grows it from sm) so the onboarding checklist can sit left of it.
  const [mainBtnEl, setMainBtnEl] = useState<HTMLButtonElement | null>(null);
  const setMainBtnRef = useCallback((el: HTMLButtonElement | null): void => {
    mainBtnRef.current = el;
    setMainBtnEl(el);
  }, []);
  usePublishFloatingHeight(mainBtnEl, '--floating-fab-w', 12, 'width');

  // ── ESC key handler ──
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape' && !e.defaultPrevented) {
        onClose();
        mainBtnRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  // ── Outside click handler ──
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    // Small delay so the toggle click doesn't immediately re-close
    const id = setTimeout(() => {
      document.addEventListener('mousedown', handler);
    }, 50);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', handler);
    };
  }, [isOpen, onClose]);

  // ── Focus trap: Tab key cycles within FAB + actions ──
  const handleKeyDown = useCallback(
    (e: ReactKeyboardEvent<HTMLDivElement>) => {
      if (!isOpen) return;
      if (e.key !== 'Tab') return;

      const focusable = containerRef.current?.querySelectorAll<HTMLElement>(
        'button:not([disabled])'
      );
      if (!focusable || focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [isOpen]
  );

  // ── Handle action click: close first, then fire ──
  const handleActionClick = (actionId: SpeedDialActionId) => {
    onClose();
    // Small delay lets the speed dial close animation start before modal opens
    setTimeout(() => onAction(actionId), 120);
  };

  const prefersReducedMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <div
      ref={containerRef}
      // The portfolio BottomNav has two destinations. On mobile the FAB uses
      // their empty centre slot, leaving the brand cards fully uncovered.
      // On desktop it returns to the bottom-right corner.
      // pointer-events-none on the wrapper lets clicks pass through the empty
      // space around the FAB; the toggle button + open menu re-enable pointer
      // events explicitly so they remain clickable.
      // NO transform on this wrapper: a transformed ancestor would become the
      // containing block of the position:fixed scrim below. Centring uses
      // left/right 0 + mx-auto + w-fit instead.
      className={`fixed flex flex-col items-end gap-2 pointer-events-none ${
        centerOnMobile
          ? 'bottom-[calc(env(safe-area-inset-bottom)+0.25rem)] sm:bottom-[calc(env(safe-area-inset-bottom)+1rem)] lg:bottom-6 left-0 right-0 mx-auto w-fit lg:left-auto lg:right-6 lg:mx-0 z-[60] lg:z-40'
          : 'bottom-[calc(var(--bottom-nav-h)+env(safe-area-inset-bottom)+1rem+var(--floating-stack-offset))] lg:bottom-6 right-4 sm:right-6 z-40'
      }`}
      onKeyDown={handleKeyDown}
    >
      {/* ── Scrim - subtle backdrop behind actions ─────────────────────── */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[-1] bg-black/10 backdrop-blur-[2px]"
          aria-hidden="true"
          onClick={onClose}
          style={prefersReducedMotion ? {} : undefined}
        />
      )}

      {/* ── Action buttons - sprout upward ─────────────────────────────── */}
      {/* Rendered top-to-top: send_digest (top) first in DOM, compose (bottom) last.
          Each action offset by its sprout-order index for staggered timing. */}
      <div
        role="menu"
        aria-label={t('fab_speed_dial_menu', { defaultValue: 'Quick actions' })}
        className={`${centerOnMobile ? 'absolute bottom-full right-0 mb-[calc(0.5rem+var(--floating-stack-offset)+min(var(--floating-stack-offset),8px))] lg:static lg:mb-0' : ''} flex flex-col-reverse items-end gap-2 overflow-hidden ${
          isOpen ? 'pointer-events-auto' : 'pointer-events-none'
        }`}
        aria-hidden={!isOpen}
      >
        {[...ACTIONS].filter((a) => !hiddenActions?.includes(a.id)).reverse().map((action) => {
          const sproutIndex = SPROUT_ORDER.indexOf(action.id);
          const delayMs = isOpen ? sproutIndex * 50 : (SPROUT_ORDER.length - 1 - sproutIndex) * 50;

          const isVisible = isOpen;
          const lockedFeature = lockedActions?.[action.id];
          const actionLabel = t(action.labelKey, { defaultValue: action.defaultLabel });

          return (
            <div
              key={action.id}
              className={`flex items-center gap-3 transition-all ${
                prefersReducedMotion
                  ? ''
                  : 'duration-200'
              } ${
                isVisible
                  ? 'opacity-100 translate-y-0 pointer-events-auto'
                  : 'opacity-0 translate-y-2 pointer-events-none'
              }`}
              style={
                prefersReducedMotion
                  ? { visibility: isVisible ? 'visible' : 'hidden' }
                  : {
                      transitionDelay: `${delayMs}ms`,
                    }
              }
            >
              {/* Label pill */}
              <span
                className="
                  text-xs font-semibold text-gray-700 bg-white/95
                  px-3 py-1.5 rounded-full shadow-md border border-gray-100
                  select-none whitespace-nowrap
                "
                aria-hidden="true"
              >
                {t(action.labelKey, { defaultValue: action.defaultLabel })}
              </span>

              {/* Icon button */}
              <button
                type="button"
                role="menuitem"
                aria-label={
                  lockedFeature
                    ? t('common:gating_locked_label', { feature: actionLabel })
                    : t(action.labelKey, { defaultValue: action.ariaLabel })
                }
                {...(lockedFeature ? { 'data-testid': `locked-entry-${lockedFeature}` } : {})}
                className={`
                  relative w-11 h-11 rounded-full ${action.accentText} shadow-lg flex items-center justify-center shrink-0
                  transition-all duration-150 active:scale-95
                  focus:outline-none border-2 border-transparent
                  ${action.accentBg}
                  ${action.accentRing}
                `}
                onClick={() => handleActionClick(action.id)}
                tabIndex={isOpen ? 0 : -1}
              >
                <i className={`hm ${action.iconClass}`} style={{ fontSize: 18 }} aria-hidden="true" />
                {lockedFeature && <LockBadge size="md" className="absolute -right-1 -top-1" />}
              </button>
            </div>
          );
        })}
      </div>

      {/* ── Main FAB ───────────────────────────────────────────────────── */}
      <button
        ref={setMainBtnRef}
        type="button"
        role="button"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label={
          isOpen
            ? t('fab_close_label', { defaultValue: 'Close quick actions' })
            : t('fab_open_label', { defaultValue: 'Quick actions' })
        }
        className={`
          pointer-events-auto
          flex items-center justify-center gap-2 rounded-full shadow-lg
          w-14 h-14 sm:w-auto sm:h-auto sm:px-3.5 sm:py-2.5
          bg-primary-500 text-primary-contrast font-semibold text-sm
          hover:bg-primary-600 active:scale-95
          transition-all duration-200
          focus:outline-none border-2 border-transparent focus-visible:border-gray-900
          ${isOpen ? 'bg-primary-600' : ''}
        `}
        onClick={onToggle}
      >
        {/* Icon rotates 45° when open (plus → cross).
            No `display` override here - the global .hm rule provides
            inline-flex centering; transform works on inline-flex too. */}
        <i
          className="hm hm-plus-open block leading-none"
          style={{
            fontSize: 18,
            lineHeight: 1,
            transition: prefersReducedMotion ? 'none' : 'transform 250ms cubic-bezier(0.4, 0, 0.2, 1)',
            transform: isOpen ? 'rotate(45deg)' : 'rotate(0deg)',
          }}
          aria-hidden="true"
        />
        <span className="hidden sm:inline">
          {isOpen
            ? t('fab_close_label', { defaultValue: 'Close' })
            : t('fab_open_label', { defaultValue: 'Quick actions' })}
        </span>
      </button>
    </div>
  );
};

export default SpeedDialFab;
